import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { cloneDeep } from "lodash";
// Components
import { Button } from "components/Buttons";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "layouts/Dialog";
import SectionTheme from "pages/OwnerPage/QuizFormPage/TabContentQuiz/Theme";
import SectionImages from "pages/OwnerPage/QuizFormPage/TabContentQuiz/SectionImages";
import QuestionsSettings from "pages/OwnerPage/QuizFormPage/TabContentQuiz/QuestionsSettings";
// Context
import { usePausePreviewSync } from "./PreviewSyncContext";
import { useCommitVersion } from "./VersionHistoryContext";

type Props = {
  open: boolean;
  onClose: () => void;
}

// Mirrors QuizPageEditDialog's pause/snapshot/cancel pattern, but isn't tied
// to a single preview screen — it edits `theme.*` and `logoImage`/
// `sponsorImage`/`backgroundImage`, all shared across every screen (see
// SHARED_VISUAL_FIELDS in QuizAgentChatScreen), so Apply refreshes every
// preview at once.
const QuizStyleEditDialog:FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();

  usePausePreviewSync();

  const commitVersion = useCommitVersion();

  const methods = useFormContext<any>();

  const [snapshot] = useState(() => cloneDeep(methods.getValues()));

  const handleClose = () => {
    methods.reset(snapshot);
    onClose();
  };

  const handleApply = () => {
    commitVersion(`Edited: ${t('dialogs.quizAgent.styleEditorTitle')}`);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogTitle title={t('dialogs.quizAgent.styleEditorTitle')} onClose={handleClose} />
      <DialogContent>
        <SectionTheme defaultExpanded />
        <SectionImages />
        <QuestionsSettings styleOnly />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} className="mr-2">
          {t('buttons.cancel')}
        </Button>
        <Button variant="contained" onClick={handleApply}>
          {t('buttons.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuizStyleEditDialog;
